import React, { useMemo, useState } from 'react';
import { apiRequest } from '../../services/apiClient';
import Button from '../ui/Button';
import DirectorPanelState from './DirectorPanelState';

function summarizeErrors(errors = []) {
  return errors.slice(0, 5);
}

function DirectorCsvImportCard({
  entityType,
  title,
  description,
  columnHint,
  onApplied
}) {
  const [fileName, setFileName] = useState('');
  const [csvText, setCsvText] = useState('');
  const [preview, setPreview] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isApplyLoading, setIsApplyLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const topErrors = useMemo(() => summarizeErrors(preview?.errors || []), [preview]);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const text = await file.text();
    setFileName(file.name);
    setCsvText(text);
    setPreview(null);
    setMessage('');
    setError('');
  };

  const handlePreview = async () => {
    if (!csvText) {
      setError('Choose a CSV file before previewing.');
      return;
    }

    setIsPreviewLoading(true);
    setError('');
    setMessage('');

    try {
      const payload = await apiRequest('/api/performance-import/preview', {
        method: 'POST',
        body: JSON.stringify({
          entityType,
          csvText,
          fileName
        })
      });

      setPreview(payload);
      setMessage(`Preview ready. ${payload.summary.validRows} valid row(s), ${payload.summary.invalidRows} invalid row(s).`);
    } catch (requestError) {
      setError(requestError.message || 'Unable to preview CSV.');
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleApply = async () => {
    if (!preview?.previewToken || !csvText) {
      setError('Preview the CSV before applying it.');
      return;
    }

    setIsApplyLoading(true);
    setError('');
    setMessage('');

    try {
      const payload = await apiRequest('/api/performance-import/apply', {
        method: 'POST',
        body: JSON.stringify({
          entityType,
          csvText,
          fileName,
          previewToken: preview.previewToken
        })
      });

      setPreview(payload);
      setMessage(`Applied ${payload.applied.affectedCount} row(s). Created ${payload.applied.createCount}, updated ${payload.applied.updateCount}.`);
      if (onApplied) {
        onApplied(payload);
      }
    } catch (requestError) {
      setError(requestError.message || 'Unable to apply CSV import.');
    } finally {
      setIsApplyLoading(false);
    }
  };

  return (
    <section className="director-card">
      <div className="role-section-header">
        <h2>{title}</h2>
        <span>csv import</span>
      </div>
      <p className="role-section-copy">{description}</p>
      <p className="director-muted">
        Columns: <code>{columnHint}</code>
      </p>
      <label className="director-upload-card">
        <span>{fileName || 'Choose CSV File'}</span>
        <input type="file" accept=".csv,text/csv" onChange={handleFileChange} />
      </label>
      <div className="director-form-actions director-form-actions-spread">
        <Button size="sm" variant="secondary" onClick={handlePreview} disabled={!csvText} loading={isPreviewLoading} loadingLabel="Previewing...">
          Preview Import
        </Button>
        <Button onClick={handleApply} disabled={!preview?.previewToken} loading={isApplyLoading} loadingLabel="Applying...">
          Apply Valid Rows
        </Button>
      </div>

      <DirectorPanelState kind="success" message={!error ? message : ''} />
      <DirectorPanelState kind="error" message={error} />

      {preview && (
        <div className="director-import-preview">
          <div className="director-import-summary">
            <div className="director-import-stat">
              <span>Total</span>
              <strong>{preview.summary.totalRows}</strong>
            </div>
            <div className="director-import-stat">
              <span>Create</span>
              <strong>{preview.summary.createCount}</strong>
            </div>
            <div className="director-import-stat">
              <span>Update</span>
              <strong>{preview.summary.updateCount}</strong>
            </div>
            <div className="director-import-stat">
              <span>Unchanged</span>
              <strong>{preview.summary.unchangedCount || 0}</strong>
            </div>
            <div className="director-import-stat">
              <span>Errors</span>
              <strong>{preview.summary.invalidRows}</strong>
            </div>
          </div>
          {topErrors.length > 0 && (
            <div className="director-import-errors">
              <strong>Top Errors</strong>
              <ul className="director-bullet-list">
                {topErrors.map(item => (
                  <li key={`${item.rowNumber}-${item.field}-${item.message}`}>
                    Row {item.rowNumber}: {item.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default DirectorCsvImportCard;
